import { type RefObject, useState } from 'react';
import useResizeObserver from 'use-resize-observer';

import useDebouncedCallback from './useDebouncedCallback';

/**
 * A debounced version of useResizeObserver that helps prevent ResizeObserver loop errors
 * by debouncing the resize notifications.
 *
 * @template T - The type of HTML element being observed
 * @param ref - Optional ref to use instead of creating a new one
 * @param debounceMs - The debounce delay in milliseconds (default: 100)
 * @returns Object containing width, height, and ref
 */
export function useDebouncedResizeObserver<T extends HTMLElement = HTMLDivElement>(
  ref?: RefObject<T>,
  debounceMs: number = 50,
) {
  const [dimensions, setDimensions] = useState<{ width?: number; height?: number }>({});

  const debouncedResize = useDebouncedCallback(({ width, height }: { width?: number; height?: number }) => {
    setDimensions({ width, height });
  }, debounceMs);

  const { ref: resizeRef } = useResizeObserver<T>({
    ref,
    onResize: debouncedResize,
  });

  return {
    ...dimensions,
    ref: ref || resizeRef,
  };
}
